import { Fragment, useEffect, useMemo, useState } from "react"
import { useDropzone } from "react-dropzone"
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap"
import { toast } from "react-toastify"
import * as Yup from "yup"
import { useFormik } from "formik"
import ThemeModal from "components/Common/ThemeModal"
import { CopyButton } from "components/Common/CopyButton"
import InputField from "components/Common/InputField"
import FormikCreatableField from "components/Common/FormikCreatableField"
import VehicleLink from "components/Common/VehicleLink"
import { extractAuctionSheet, onboardAuctionSheet } from "api/auctionSheetApi"
import {
  getExtractedShortDescription,
  parseAuctionSheetJson,
} from "constants/auctionSheetExtraction"
import { useToastMessage } from "hooks/useToastMessage"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"

const AUCTION_SHEET_ACCEPT = { "image/*": [".jpg", ".jpeg", ".png", ".webp"] }

const STEPS = [
  { number: 1, label: "Upload & Scan" },
  { number: 2, label: "Review & Edit" },
  { number: 3, label: "Complete" },
]

const AUCTION_SHEET_STORAGE_KEY = "auctionSheetExtract"

const EXTRACT_STATUSES = [
  "Reading auction sheet...",
  "Detecting chassis number...",
  "Recognizing vehicle specs...",
  "Extracting engine & technical details...",
  "Identifying colors & features...",
  "Compiling short description...",
  "Almost done...",
]

const toValues = result => ({
  ...(result || {}),
  features: Array.isArray(result && result.features) ? [...result.features] : [],
})

const buildShortDescription = values => {
  const head = [values.brand, values.model, values.package]
    .filter(Boolean)
    .join(" ")

  const parts = []

  if (head) parts.push(head)

  ;(values.features || []).forEach(feature => parts.push(feature))

  if (values.auction_grade) {
    parts.push(`AUCTION GRADE ${values.auction_grade}`)
  }

  if (values.mileage_km) {
    parts.push(`MILEAGE ${Number(values.mileage_km).toLocaleString()} KM`)
  }

  const text = parts.join(", ")

  return text ? `${text}.` : ""
}

// vehicle_info keys mirror the auction-sheet onboard payload (AuctionSheetOnboardingService)
const emptyVehicleInfo = () => ({
  chassis_no: "",
  engine_no: "",
  brand: "",
  model: "",
  model_year: "",
  color: "",
  interior_color: "",
  package: "",
  cc: "",
  fuel_type: "",
  tyre_size: "",
  mileage: "",
  doors: "",
  seats: "",
  location: "",
  chassis_code: "",
  engine_code: "",
  engine_type: "",
  key_type: "",
  auction_grade: "",
  transmission: "",
  short_description: "",
})

const mapResultToVehicleInfo = result => {
  const r = result || {}
  return {
    chassis_no: r.chassis_no || "",
    engine_no: r.engine_no || "",
    brand: r.brand || "",
    model: r.model || "",
    model_year: r.year || "",
    color: r.exterior_color || "",
    interior_color: r.interior_color || "",
    package: r.package || "",
    cc: r.engine_cc || "",
    fuel_type: r.fuel_type || "",
    tyre_size: r.tire_size || "",
    mileage: r.mileage_km ?? "",
    doors: r.doors ?? "",
    seats: r.seats ?? "",
    location: r.location || "",
    chassis_code: r.chassis_code || "",
    engine_code: r.engine_code || "",
    engine_type: r.engine_type || "",
    key_type: r.key_type || "",
    auction_grade: r.auction_grade || "",
    transmission: r.transmission || "",
    short_description: r.short_description || "",
  }
}

const validationSchema = Yup.object({
  chassis_no: Yup.string().trim().required("Chassis number is required"),
  engine_no: Yup.string().nullable(),
  brand: Yup.string().nullable(),
  model: Yup.string().nullable(),
  model_year: Yup.string().nullable(),
  color: Yup.string().nullable(),
  interior_color: Yup.string().nullable(),
  package: Yup.string().nullable(),
  cc: Yup.string().nullable(),
  fuel_type: Yup.string().nullable(),
  tyre_size: Yup.string().nullable(),
  mileage: Yup.number().typeError("Mileage must be numeric").nullable(),
  doors: Yup.number().typeError("Doors must be numeric").nullable(),
  seats: Yup.number().typeError("Seats must be numeric").nullable(),
  location: Yup.string().nullable(),
  chassis_code: Yup.string().nullable(),
  engine_code: Yup.string().nullable(),
  engine_type: Yup.string().nullable(),
  key_type: Yup.string().nullable(),
  auction_grade: Yup.string().nullable(),
  transmission: Yup.string().nullable(),
  short_description: Yup.string().nullable(),
})

const AuctionSheetExtractModal = ({ isOpen, toggle, onOnboarded }) => {
  const [step, setStep] = useState(1)
  const [file, setFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [isExtracting, setIsExtracting] = useState(false)
  const [result, setResult] = useState(null)
  const [raw, setRaw] = useState("")
  const [values, setValues] = useState(toValues(null))
  const [newFeature, setNewFeature] = useState("")
  const [onboarded, setOnboarded] = useState(false)
  const [onboardedVehicle, setOnboardedVehicle] = useState(null)
  const [statusIndex, setStatusIndex] = useState(0)
  const [extractError, setExtractError] = useState(null)
  const [onboarding, setOnboarding] = useState(false)
  const { handleError } = useToastMessage()

  const {
    brands,
    models,
    years,
    exteriorColors,
    interiorColors,
    packages,
    fuelTypes,
    engineCCs,
    engineTypes,
    keyTypes,
    auctionGrades,
    transmissions,
    locations,
    isLoading: vehicleOptionsLoading,
  } = useFilteredVehicleOptions({ isAll: true })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: emptyVehicleInfo(),
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: () => {},
  })

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: AUCTION_SHEET_ACCEPT,
    maxFiles: 1,
    multiple: false,
    disabled: isExtracting,
    onDrop: acceptedFiles => {
      const [accepted] = acceptedFiles
      if (!accepted) return
      setFile(accepted)
      setResult(null)
      setRaw("")
      setValues(toValues(null))
      setExtractError(null)
      sessionStorage.removeItem(AUCTION_SHEET_STORAGE_KEY)
      formik.resetForm({ values: emptyVehicleInfo() })
    },
  })

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null)
      return
    }

    const url = URL.createObjectURL(file)
    setPreviewUrl(url)

    return () => URL.revokeObjectURL(url)
  }, [file])

  const saveExtraction = (parsed, rawText) => {
    try {
      sessionStorage.setItem(
        AUCTION_SHEET_STORAGE_KEY,
        JSON.stringify({ result: parsed, raw: rawText, savedAt: Date.now() })
      )
    } catch {
      // sessionStorage unavailable — extraction still works in-memory
    }
  }

  const restoreStoredExtraction = () => {
    let stored = null
    try {
      stored = JSON.parse(sessionStorage.getItem(AUCTION_SHEET_STORAGE_KEY))
    } catch {
      sessionStorage.removeItem(AUCTION_SHEET_STORAGE_KEY)
    }
    if (!stored || !stored.result) return

    setResult(stored.result)
    setRaw(stored.raw || "")
    setValues(toValues(stored.result))
    formik.setValues(mapResultToVehicleInfo(stored.result))
    setStep(2)
  }

  useEffect(() => {
    if (!isOpen) {
      setOnboarded(false)
      setOnboardedVehicle(null)
      return
    }
    restoreStoredExtraction()
  }, [isOpen])

  useEffect(() => {
    setValues(toValues(result))
  }, [result])

  useEffect(() => {
    if (!isExtracting) return
    setStatusIndex(0)
    const interval = setInterval(
      () => setStatusIndex(i => Math.min(i + 1, EXTRACT_STATUSES.length - 1)),
      9000
    )
    return () => clearInterval(interval)
  }, [isExtracting])

  const features = values.features || []
  const shortDescription = useMemo(
    () => getExtractedShortDescription(values),
    [values]
  )

  const handleSync = () => {
    const description = buildShortDescription(values)
    setValues(prev => ({
      ...(prev || {}),
      short_description: description,
    }))
    formik.setFieldValue("short_description", description)
  }

  const handleAddFeature = () => {
    const feature = newFeature.trim()
    if (!feature) return

    setValues(prev => ({
      ...(prev || {}),
      features: [...(prev.features || []), feature],
    }))
    setNewFeature("")
  }

  const handleRemoveFeature = index => {
    setValues(prev => ({
      ...(prev || {}),
      features: (prev.features || []).filter((_, featureIndex) => featureIndex !== index),
    }))
  }

  const handleExtract = async () => {
    if (!file || isExtracting) return

    try {
      setIsExtracting(true)
      setExtractError(null)
      const res = await extractAuctionSheet(file, true)
      const data =
        res && res.data && typeof res.data === "object" ? res.data : null
      const parsed = data || parseAuctionSheetJson(res && res.raw)
      const rawText = (res && res.raw) || ""
      setResult(parsed)
      setRaw(rawText)
      setValues(toValues(parsed))
      saveExtraction(parsed, rawText)
      if (parsed) {
        formik.setValues(mapResultToVehicleInfo(parsed))
        setStep(2)
      }
      if (getExtractedShortDescription(parsed)) {
        toast.success("Vehicle details read from the sheet.")
      } else if (rawText) {
        toast.info("Extracted, but couldn't parse a structured result. Please review the fields or scan again.")
      } else {
        toast.info("Extracted, but no description was produced.")
      }
    } catch (error) {
      const isGeminiTimeout =
        error?.response?.status === 502 &&
        /cURL error 28|timed out/i.test(error?.response?.data?.message || "")
      if (isGeminiTimeout) {
        const friendly =
          "The scanner took too long and timed out. This can happen on busy moments — please try again."
        setExtractError(friendly)
        toast.error(friendly)
      } else {
        handleError(error, "Failed to scan the auction sheet.")
      }
    } finally {
      setIsExtracting(false)
    }
  }

  const handleNext = async () => {
    const errors = await formik.validateForm()
    if (Object.keys(errors).length > 0) {
      formik.setTouched(
        Object.fromEntries(Object.keys(errors).map(key => [key, true]))
      )
      toast.error("Please fix the highlighted fields before continuing.")
      return
    }
    setStep(3)
  }

  const resetExtractionState = () => {
    setFile(null)
    setResult(null)
    setRaw("")
    setValues(toValues(null))
    setNewFeature("")
    setOnboarded(false)
    setOnboardedVehicle(null)
    setExtractError(null)
    sessionStorage.removeItem(AUCTION_SHEET_STORAGE_KEY)
    formik.resetForm({ values: emptyVehicleInfo() })
    setStep(1)
  }

  const handleOnboard = async () => {
    if (onboarding) return

    const payload = {
      ...formik.values,
      features: features,
      short_description: formik.values.short_description,
    }

    try {
      setOnboarding(true)
      const res = await onboardAuctionSheet(payload)
      toast.success(res?.message ?? "Vehicle onboarded successfully.")
      sessionStorage.removeItem(AUCTION_SHEET_STORAGE_KEY)
      setOnboardedVehicle(res?.data || null)
      setOnboarded(true)
      onOnboarded?.(res?.data)
    } catch (error) {
      handleError(error, "Failed to onboard vehicle.")
    } finally {
      setOnboarding(false)
    }
  }

  const handleScanAnother = () => {
    resetExtractionState()
  }

  const handleNewScan = () => {
    resetExtractionState()
    open()
  }

  const renderStepper = () => (
    <div className="mb-4 d-flex align-items-start justify-content-between gap-3">
      <div className="d-flex align-items-start w-100">
        {STEPS.map((s, i) => (
          <Fragment key={s.number}>
            {i > 0 && (
              <div
                className="flex-grow-1"
                style={{
                  height: 2,
                  marginTop: 15,
                  background: step >= s.number ? "#059669" : "#e5e7eb",
                }}
              />
            )}
            <div
              className="d-flex flex-column align-items-center"
              style={{ width: 140 }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: step >= s.number ? "#059669" : "#e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ffffff",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                }}
              >
                {s.number}
              </div>
              <span
                className="mt-1 text-center"
                style={{
                  fontSize: "0.875rem",
                  color: step === s.number ? "#059669" : "#6b7280",
                  fontWeight: step === s.number ? 600 : 400,
                }}
              >
                {s.label}
              </span>
            </div>
          </Fragment>
        ))}
      </div>
      {result && (
        <Button
          size="sm"
          color="light"
          outline
          type="button"
          onClick={handleNewScan}
          disabled={isExtracting}
        >
          <i className="bx bx-reset me-1" />
          New Scan
        </Button>
      )}
    </div>
  )

  const renderSummaryRow = (label, value) => (
    <Col md={6} className="mb-2">
      <div className="border rounded bg-light px-3 py-2 h-100">
        <small className="text-muted d-block">{label}</small>
        <strong className="d-block text-truncate">{value || "—"}</strong>
      </div>
    </Col>
  )

  const renderStepOne = () => (
    <div
      {...getRootProps()}
      className={`border rounded p-4 text-center cursor-pointer ${
        isDragActive ? "border-primary bg-light" : "border-dashed"
      }`}
      style={{ cursor: "pointer" }}
    >
      <input {...getInputProps()} />
      {previewUrl ? (
        <div
          className={`auction-sheet-scan-wrapper rounded mb-2 ${
            isExtracting ? "is-extracting" : ""
          }`}
        >
          <img
            src={previewUrl}
            alt="Auction sheet preview"
            className="img-fluid rounded"
            style={{ maxHeight: 320 }}
          />
          {isExtracting && <div className="auction-sheet-scan-line" />}
        </div>
      ) : (
        <div className="py-4">
          <i className="bx bx-upload display-4 text-muted" />
          <p className="mb-0 mt-2">
            {isDragActive
              ? "Drop the auction sheet here"
              : "Drag & drop your auction sheet image, or click to browse"}
          </p>
        </div>
      )}
      {file && (
        <div className="mt-1 d-flex align-items-center justify-content-center gap-2">
          <small className="text-muted">{file.name}</small>
          {result && !isExtracting && (
            <Badge color="success" className="border-0 d-inline-flex align-items-center gap-1">
              <i className="bx bx-check" />
              Extracted
            </Badge>
          )}
        </div>
      )}
      {isExtracting && (
        <div className="auction-sheet-extract-status">
          <i className="bx bx-loader-alt bx-spin me-2" />
          <span key={statusIndex} className="auction-sheet-extract-status__text">
            {EXTRACT_STATUSES[statusIndex]}
          </span>
        </div>
      )}
      {extractError && (
        <Alert
          color="danger"
          className="mt-3 mb-0 text-start"
          onClick={e => e.stopPropagation()}
        >
          <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
            <span>
              <i className="bx bx-error-circle me-1" />
              {extractError}
            </span>
            <Button
              size="sm"
              color="danger"
              outline
              type="button"
              onClick={handleExtract}
              disabled={isExtracting}
            >
              <i className="bx bx-reset me-1" />
              Retry
            </Button>
          </div>
        </Alert>
      )}
    </div>
  )

  const renderField = (name, label, required, type) => (
    <Col md={6}>
      <div className="mb-3">
        <Label htmlFor={name}>
          {label} {required && <span className="text-danger">*</span>}
        </Label>
        <InputField
          type={type || "text"}
          name={name}
          id={name}
          value={formik.values[name]}
          onChange={formik.handleChange}
          invalid={formik.touched[name] && !!formik.errors[name]}
        />
        {formik.touched[name] && formik.errors[name] && (
          <FormFeedback type="invalid">{formik.errors[name]}</FormFeedback>
        )}
      </div>
    </Col>
  )

  const renderCreatableField = (name, label, data, placeholder) => (
    <Col md={6}>
      <FormikCreatableField
        name={name}
        label={label}
        formik={formik}
        data={data}
        valueKey="name"
        labelKey="name"
        isLoading={vehicleOptionsLoading}
        placeholder={placeholder || `Select or type ${label.toLowerCase()}`}
      />
    </Col>
  )

  const renderSectionTitle = title => (
    <strong className="d-block mb-2 mt-3 text-uppercase small text-muted">
      {title}
    </strong>
  )

  const renderStepTwo = () => (
    <Form>
      {renderSectionTitle("Identification")}
      <Row>
        {renderField("chassis_no", "Full Chassis No.", true)}
        {renderField("engine_no", "Engine No.")}
        {renderField("chassis_code", "Chassis Code")}
        {renderField("engine_code", "Engine Code")}
      </Row>

      {renderSectionTitle("Specifications")}
      <Row>
        {renderCreatableField("brand", "Brand", brands)}
        {renderCreatableField("model", "Model", models)}
        {renderCreatableField("model_year", "Year / Model", years)}
        {renderCreatableField("package", "Package", packages)}
        {renderCreatableField("color", "Color", exteriorColors)}
        {renderCreatableField("interior_color", "Interior Color", interiorColors)}
        {renderField("mileage", "Mileage (KM)", false, "number")}
        {renderField("doors", "Doors", false, "number")}
        {renderField("seats", "Seats", false, "number")}
        {renderField("tyre_size", "Tyre Size")}
        {renderCreatableField("location", "Location", locations)}
      </Row>

      {renderSectionTitle("Technical Details")}
      <Row>
        {renderCreatableField("cc", "Engine CC", engineCCs)}
        {renderCreatableField("fuel_type", "Fuel Type", fuelTypes)}
        {renderCreatableField("engine_type", "Engine Type", engineTypes)}
        {renderCreatableField("key_type", "Key Type", keyTypes)}
        {renderCreatableField("auction_grade", "Auction Grade", auctionGrades)}
        {renderCreatableField("transmission", "Transmission", transmissions)}
      </Row>

      <div className="d-flex align-items-center justify-content-between mb-2 mt-3">
        <strong className="text-uppercase small text-muted">Short Description</strong>
        <div className="d-flex gap-2 align-items-center">
          <Button size="sm" color="light" type="button" onClick={handleSync}>
            <i className="bx bx-sync me-1" />
            Sync from fields
          </Button>
          <CopyButton text={shortDescription} title="Copy short description" />
        </div>
      </div>
      <Input
        type="textarea"
        rows={3}
        name="short_description"
        value={formik.values.short_description || ""}
        onChange={formik.handleChange}
        className="bg-light border rounded"
        style={{ resize: "vertical" }}
      />

      <div className="mt-3">
        <strong className="d-block mb-1">Features</strong>
        <div className="d-flex flex-wrap gap-1 mb-2">
          {features.map((feature, index) => (
            <Badge
              key={`${feature}-${index}`}
              className="badge-soft-info border-0 d-inline-flex align-items-center gap-1"
            >
              {feature}
              <span
                role="button"
                className="cursor-pointer"
                onKeyDown={e => {
                  if (e.key === "Enter") handleRemoveFeature(index)
                }}
                tabIndex={0}
                onClick={() => handleRemoveFeature(index)}
                title="Remove feature"
              >
                &times;
              </span>
            </Badge>
          ))}
          {features.length === 0 && (
            <small className="text-muted">No features yet.</small>
          )}
        </div>
        <div className="d-flex gap-2">
          <Input
            bsSize="sm"
            placeholder="Add a feature..."
            value={newFeature}
            onChange={e => setNewFeature(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") handleAddFeature()
            }}
          />
          <Button
            size="sm"
            color="primary"
            type="button"
            onClick={handleAddFeature}
            disabled={!newFeature.trim()}
          >
            <i className="bx bx-plus me-1" />
            Add
          </Button>
        </div>
      </div>
    </Form>
  )

  const renderStepThree = () => {
    if (onboarded) {
      const infoLine = [
        onboardedVehicle?.brand?.name,
        onboardedVehicle?.model?.name,
        onboardedVehicle?.year?.name,
      ]
        .filter(Boolean)
        .join(" ")

      return (
        <div className="text-center py-4">
          <i
            className="bx bx-check-circle text-success"
            style={{ fontSize: "3.5rem" }}
          />
          <h5 className="mt-2 mb-1">Vehicle onboarded!</h5>
          <p className="text-muted mb-0">
            <VehicleLink
              vehicle={onboardedVehicle}
              name={onboardedVehicle?.full_chassis || formik.values.chassis_no}
              newTab
            />
          </p>
          {onboardedVehicle?.stock_id && (
            <div className="mt-3">
              <Badge color="success" className="badge-soft-success border-0">
                <i className="bx bx-car me-1" />
                Stock ID:{" "}
                <VehicleLink
                  vehicle={onboardedVehicle}
                  name={onboardedVehicle.stock_id}
                  newTab
                />
              </Badge>
            </div>
          )}
          <div className="mt-2 text-muted small">
            {infoLine && <div>{infoLine}</div>}
            {onboardedVehicle?.mileage
              ? `${Number(onboardedVehicle.mileage).toLocaleString()} KM`
              : ""}
          </div>
        </div>
      )
    }

    return (
      <>
        <p className="text-muted small mb-3">
          Review the details below, then confirm to onboard this vehicle into stock.
        </p>
        <Row>
          {renderSummaryRow("Full Chassis No.", formik.values.chassis_no)}
          {renderSummaryRow("Engine No.", formik.values.engine_no)}
          {renderSummaryRow("Brand", formik.values.brand)}
          {renderSummaryRow("Model", formik.values.model)}
          {renderSummaryRow("Year / Model", formik.values.model_year)}
          {renderSummaryRow("Package", formik.values.package)}
          {renderSummaryRow("Color", formik.values.color)}
          {renderSummaryRow("Interior Color", formik.values.interior_color)}
          {renderSummaryRow("Engine CC", formik.values.cc)}
          {renderSummaryRow("Fuel Type", formik.values.fuel_type)}
          {renderSummaryRow("Engine Type", formik.values.engine_type)}
          {renderSummaryRow("Key Type", formik.values.key_type)}
          {renderSummaryRow("Auction Grade", formik.values.auction_grade)}
          {renderSummaryRow("Transmission", formik.values.transmission)}
          {renderSummaryRow("Chassis Code", formik.values.chassis_code)}
          {renderSummaryRow("Engine Code", formik.values.engine_code)}
          {renderSummaryRow("Doors", formik.values.doors)}
          {renderSummaryRow("Seats", formik.values.seats)}
          {renderSummaryRow("Tyre Size", formik.values.tyre_size)}
          {renderSummaryRow("Location", formik.values.location)}
          {renderSummaryRow(
            "Mileage (KM)",
            formik.values.mileage ? Number(formik.values.mileage).toLocaleString() : ""
          )}
          <Col md={6} className="mb-2">
            <div className="border rounded bg-light px-3 py-2 h-100">
              <small className="text-muted d-block">Short Description</small>
              <strong className="d-block text-truncate">
                {formik.values.short_description || "—"}
              </strong>
            </div>
          </Col>
        </Row>
        {features.length > 0 && (
          <div className="mt-2">
            <small className="text-muted d-block mb-1">Features</small>
            <div className="d-flex flex-wrap gap-1">
              {features.map((feature, index) => (
                <Badge key={`${feature}-${index}`} className="badge-soft-info border-0">
                  {feature}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <ThemeModal
      isOpen={isOpen}
      toggle={toggle}
      title="Add Vehicle from Auction Sheet"
      size="xl"
      footer={
        <div className="d-flex justify-content-between w-100">
          {step === 1 && (
            <>
              <Button color="light" type="button" onClick={toggle}>
                Close
              </Button>
              <div className="d-flex gap-2">
                {result && !isExtracting && (
                  <>
                    <Button
                      color="light"
                      outline
                      type="button"
                      onClick={handleExtract}
                      disabled={!file || isExtracting}
                      title={!file ? "Upload the sheet again to re-scan" : undefined}
                    >
                      <i className="bx bx-reset me-1" />
                      Re-scan
                    </Button>
                    <Button color="primary" type="button" onClick={() => setStep(2)}>
                      Continue to Review
                      <i className="bx bx-chevron-right ms-1" />
                    </Button>
                  </>
                )}
                {(!result || isExtracting) && (
                  <Button
                    color="primary"
                    type="button"
                    onClick={handleExtract}
                    disabled={!file || isExtracting}
                  >
                    {isExtracting ? (
                      <Spinner size="sm" className="me-1" />
                    ) : (
                      <i className="bx bx-aperture me-1" />
                    )}
                    {isExtracting ? "Scanning..." : "Scan Sheet"}
                  </Button>
                )}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <Button color="light" type="button" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button color="primary" type="button" onClick={handleNext}>
                Next
                <i className="bx bx-chevron-right ms-1" />
              </Button>
            </>
          )}

          {step === 3 && !onboarded && (
            <>
              <Button color="light" type="button" onClick={() => setStep(2)}>
                Back
              </Button>
              <Button
                color="success"
                type="button"
                onClick={handleOnboard}
                disabled={onboarding}
              >
                {onboarding ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <i className="bx bx-car me-1" />
                )}
                {onboarding ? "Onboarding..." : "Onboard Vehicle"}
              </Button>
            </>
          )}

          {step === 3 && onboarded && (
            <>
              <Button color="light" type="button" onClick={handleScanAnother}>
                <i className="bx bx-reset me-1" />
                Scan Another
              </Button>
              <Button
                color="success"
                type="button"
                onClick={() => {
                  resetExtractionState()
                  toggle()
                }}
              >
                Done
              </Button>
            </>
          )}
        </div>
      }
    >
      {renderStepper()}

      {step === 1 && renderStepOne()}
      {step === 2 && renderStepTwo()}
      {step === 3 && renderStepThree()}
    </ThemeModal>
  )
}

export default AuctionSheetExtractModal